/**
 * @param {string} "data-video" - src of youtube video we want to inject
 * @param {string} "data-thumbnail-src" - src of image to use as video thumbnail/background
 * @param {string} "data-thumbnail-alt" - alt attribute of image to use as video thumbnail/background
 * @param {string} "data-button-aria-label" - aria label to use on video play button
 * @param {string} "data-duration" - label to show length of video, should display as "HH:MM"
 */

import { ready } from '~/core'
import createElement from '~/utils/createElement'
import addEvents from '~/utils/addEvents'

ready('modal-video-click', '.video--modal', (video) => {
    const thumbnail = createThumbnailImage()
    const playButton = createPlayButton()
    const videoDuration = createVideoDuration()

    if (thumbnail) video.appendChild(thumbnail)

    if (playButton) video.appendChild(playButton)

    if (videoDuration) video.appendChild(videoDuration)

    let frame = video.querySelector('.video__frame')

    if (!frame) {
        frame = document.createElement('iframe')
        frame.classList.add('video__frame')
        frame.setAttribute('playsinline', '')
    }

    const modal = document.createElement('div')
    const src = video.dataset.video
    let isPlaying = false

    const closeButton = createElement('div', {
        class: 'close-button',
        style: `
            background-image: url("data:image/svg+xml,%3Csvg fill='white' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'%3E%3Cpath d='M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z'/%3E%3C/svg%3E%0A");
            background-repeat: no-repeat;
            height: 30px;
            width: 30px;
        `,
        tabindex: '0',
    })

    const focusTrap = createElement('div', {
        class: 'focus-trap',
        tabindex: '0',
    })

    modal.classList.add('video__modal')
    modal.appendChild(closeButton)
    modal.insertAdjacentElement('afterbegin', frame)
    modal.setAttribute('tabindex', '0')
    frame.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    )
    frame.setAttribute('allowfullscreen', '')
    frame.setAttribute('enablejsapi', 'true')
    frame.setAttribute('tabindex', '0')

    const toggle = () => {
        if (isPlaying) {
            modal.remove()
            frame.src = src
        } else {
            document.body.appendChild(modal)
            modal.appendChild(focusTrap)
            let urlParams = src.indexOf('?') > -1 ? '&autoplay=1' : '?autoplay=1'
            frame.src = src + urlParams

            frame.focus()
        }

        isPlaying = !isPlaying
    }

    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target.matches('.video__modal, .close-button')) {
                toggle()
            }
        })
    }

    addEvents(frame, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            focusTrap.focus()
        }
    })

    addEvents(focusTrap, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && !e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            frame.focus()
        }

        if (isTab && e.shiftKey) {
            e.stopImmediatePropagation()
        }
    })

    addEvents(modal, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            focusTrap.focus()
        }
    })

    addEvents(closeButton, 'keydown', (e) => {
        const isEnter = (e.key || '').toLowerCase() === 'enter'

        if (isEnter) toggle()
    })

    function createThumbnailImage() {
        const img = document.createElement('img')

        img.className = 'video__poster'
        img.src = video.dataset.thumbnailSrc || 'https://img.youtube.com/vi/IpGuP50GzHY/maxresdefault.jpg' // default truliant thumbnail
        img.alt = video.dataset.thumbnailAlt || 'youtube video thumbnail'
        img.addEventListener('click', () => toggle())

        if (video.dataset.fetchPriority) {
            img.fetchPriority = video.dataset.fetchPriority
        }

        return img
    }

    function createPlayButton() {
        const button = document.createElement('button')
        button.className = 'video__play'
        button.ariaLabel = video.dataset.buttonAriaLabel || 'video play button'
        button.addEventListener('click', () => toggle())
        button.addEventListener('keydown', (e) => {
            const isEnter = (e.key || '').toLowerCase() === 'enter'

            if (isEnter) toggle()
        })

        return button
    }

    function createVideoDuration() {
        const duration = video.dataset.duration || ''

        if (!duration) return

        const durationIndicator = document.createElement('div')
        durationIndicator.className = 'video__duration'
        durationIndicator.innerText = duration

        return durationIndicator
    }
})

ready('modal-video-cta-click', '.video--modal-cta', (video) => {


    bindCTA();
    const videoDuration = createVideoDuration()


    if (videoDuration) video.appendChild(videoDuration)

    let frame = video.querySelector('.video__frame')

    if (!frame) {
        frame = document.createElement('iframe')
        frame.classList.add('video__frame')
        frame.setAttribute('playsinline', '')
    }

    const modal = document.createElement('div')
    const src = video.dataset.video
    let isPlaying = false

    const closeButton = createElement('div', {
        class: 'close-button',
        style: `
            background-image: url("data:image/svg+xml,%3Csvg fill='white' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'%3E%3Cpath d='M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z'/%3E%3C/svg%3E%0A");
            background-repeat: no-repeat;
            height: 30px;
            width: 30px;
        `,
        tabindex: '0',
    })

    const focusTrap = createElement('div', {
        class: 'focus-trap',
        tabindex: '0',
    })

    modal.classList.add('video__modal')
    modal.appendChild(closeButton)
    modal.insertAdjacentElement('afterbegin', frame)
    modal.setAttribute('tabindex', '0')
    frame.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    )
    frame.setAttribute('allowfullscreen', '')
    frame.setAttribute('enablejsapi', 'true')
    frame.setAttribute('tabindex', '0')

    const toggle = () => {
        if (isPlaying) {
            modal.remove()
            frame.src = src
        } else {
            document.body.appendChild(modal)
            modal.appendChild(focusTrap)
            let urlParams = src.indexOf('?') > -1 ? '&autoplay=1' : '?autoplay=1'
            frame.src = src + urlParams

            frame.focus()
        }

        isPlaying = !isPlaying
    }

    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target.matches('.video__modal, .close-button')) {
                toggle()
            }
        })
    }

    addEvents(frame, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            focusTrap.focus()
        }
    })

    addEvents(focusTrap, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && !e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            frame.focus()
        }

        if (isTab && e.shiftKey) {
            e.stopImmediatePropagation()
        }
    })

    addEvents(modal, 'keydown', (e) => {
        const isTab = (e.key || '').toLowerCase() === 'tab'

        if (isTab && e.shiftKey) {
            e.preventDefault()
            e.stopImmediatePropagation()
            focusTrap.focus()
        }
    })

    addEvents(closeButton, 'keydown', (e) => {
        const isEnter = (e.key || '').toLowerCase() === 'enter'

        if (isEnter) toggle()
    })


    function bindCTA() {
        if (video.dataset.triggerCta) {
            const button = document.getElementById(video.dataset.triggerCta);
            if (button) {
                addEvents(button, 'click.prevent', () => {
                    toggle();
                    return false;
                })
                //button.addEventListener('click.prevent', () => {
                //    toggle();
                //    return false;
                //})
                button.addEventListener('keydown', (e) => {
                    const isEnter = (e.key || '').toLowerCase() === 'enter'

                    if (isEnter) {
                        toggle();
                    }
                    return false;
                })
            }
        }
    }


    function createVideoDuration() {
        const duration = video.dataset.duration || ''

        if (!duration) return

        const durationIndicator = document.createElement('div')
        durationIndicator.className = 'video__duration'
        durationIndicator.innerText = duration

        return durationIndicator
    }
})
